import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { AnchorLink } from '@/components/typography/AnchorLink';
import consts from '@/config/consts';
import { useMetaTitle } from '@/hooks/useMetaTitle';
import styled from 'styled-components';
import Breadcrumbs from '../components/Breadcrumbs';
import { MainContent } from '../components/layout/MainContent';
import Page from '../components/layout/Page';
import PageContentWrapper from '../components/layout/PageContentWrapper';
import ProductList from '../components/ProductList';
import Loader from '../components/Loader';
import { getItemTitle, isWizard } from '../helpers/wizard';
import { useFetchWizardStructure } from '../hooks/useFetchData';
import { useNodes } from '../hooks/useNodes';
import { useTranslation } from '../hooks/useTranslation';
import { useValidProducts } from '../hooks/useValidProducts';
import { useMatomo } from '@datapunt/matomo-tracker-react';

const StyledMainContent = styled(MainContent)`
  margin-bottom: ${({ theme }) => theme.space.l};
`;

const Hero = styled.div`
  position: relative;
  margin: ${({ theme }) => `0 -${theme.space.s}`};

  @media (min-width: ${({ theme }) => theme.screens.l}) {
    margin: 0;
  }
`;

const Title = styled.div`
  font-family: ${({ theme }) => theme.fonts.bold};
  font-size: ${({ theme }) => theme.fontSizes.ml};
  line-height: 24px;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.elvitaTrueBlack};
  margin-left: 17px;
  margin-bottom: ${({ theme }) => theme.space.md};

  @media (min-width: ${({ theme }) => theme.screens.md}) {
    font-size: 28px;
    line-height: 33.6px;
  }

  @media (min-width: ${({ theme }) => theme.screens.l}) {
    font-size: 32px;
    line-height: 38.4px;
    margin-left: ${({ theme }) => theme.space.md};
  }
`;

const ComingSoonTitle = styled(Title)`
  margin: ${p => p.theme.space.s} auto;

  @media (min-width: ${({ theme }) => theme.screens.md}) {
    width: 50%;
  }
`;

const ComingSoonText = styled.p`
  font-family: ${p => p.theme.fonts.light};
  font-style: normal;
  font-weight: 300;
  font-size: 14px;
  line-height: 17px;
  margin: 0 auto;

  @media (min-width: ${({ theme }) => theme.screens.md}) {
    width: 50%;
  }
`;

const SearchIcon = styled.img``;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.space.l};
`;

const Wizard: React.FC = () => {
  const { trackPageView } = useMatomo();
  const { t } = useTranslation();
  const { path, childId } = useParams<{
    path: string;
    childId: string;
  }>();
  const { error, data } = useFetchWizardStructure();
  const [topLevel, setTopLevel] = React.useState<Elvita.WizardItem | undefined>(
    undefined,
  );
  useMetaTitle();

  React.useEffect(() => {
    if (data && data.status === 200) {
      setTopLevel(data?.data.find(item => item.id === path));
    }
  }, [data, path]);

  React.useEffect(() => {
    trackPageView(null);
  }, [data, trackPageView]);

  const { node, breadcrumbs } = useNodes(topLevel, childId, path, path);
  const { validItems, isLoading: validating } = useValidProducts(
    node?.children as Elvita.WizardItem[],
  );

  if (error || !node) {
    return null;
  }

  return (
    <Page hideMenu={true} hideSearch={true} isWizard={true}>
      <PageContentWrapper flex={1}>
        <Breadcrumbs
          inWizard={true}
          hideLast={true}
          breadcrumbs={breadcrumbs.slice(0, -1).map(crumb => {
            return {
              label: getItemTitle(
                crumb.label,
                data.data,
                path !== crumb.label ? path : undefined,
              ),
              url: crumb.url,
            };
          })}
        />
        <Hero>
          <Title as="h1">{node && isWizard(node) && node.title}</Title>
        </Hero>
        <StyledMainContent>
          {node?.children.length === 0 && (
            <>
              <ComingSoonTitle as="h2">
                {t('wizard.nocontent.title')}
              </ComingSoonTitle>
              <ComingSoonText>
                {t('wizard.nocontent.description')}{' '}
                <AnchorLink href={t('wizard.nocontent.link.href')}>
                  {t('wizard.nocontent.link.text')}
                </AnchorLink>
              </ComingSoonText>
            </>
          )}
          {validating ? (
            <LoaderWrapper>
              <Loader />
            </LoaderWrapper>
          ) : (
            <ProductList
              items={validItems as Elvita.WizardItem[]}
              title={t('search.categoryList') ?? 'search.categoryList'}
            />
          )}
        </StyledMainContent>
      </PageContentWrapper>
    </Page>
  );
};

export default Wizard;
